// src/components/FormBuilder.jsx//bump
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  TextField,
  Box,
  Paper,
  List,
  ListItem,
  ListItemSecondaryAction,
  Switch,
  IconButton,
  FormControlLabel,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import ModuleEditor from "./ModuleEditor";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DraggableModuleItem from "./DraggableModuleItem";
import debounce from "lodash.debounce";
import { v4 as uuidv4 } from "uuid";

// Define API base URL from environment variable (or fallback)
const API_BASE_URL = "https://formbuilderapi.getforsite.com"; //process.env.REACT_APP_API_URL ||

const CATEGORY_COLORS = {
  "Text + Layout Fields": "#ffec80",
  "User Information": "#ffec80",
  "Question Fields": "#ffec80",
  "Media Fields": "#ffec80",
  "Date/Time Fields": "#ffec80",
  "Forsite Integrations": "#ffec80",
};

const AVAILABLE_FIELDS = [
  {
    category: "Text + Layout Fields",
    fields: [
      { id: "header", type: "header", label: "Header" },
      { id: "banner", type: "banner", label: "Banner" },
      { id: "subheader", type: "subheader", label: "Subheader" },
      { id: "paragraph", type: "paragraph", label: "Paragraph" },
    ],
  },
  {
    category: "User Information",
    fields: [
      { id: "user_details", type: "user_details", label: "User Details" },
      { id: "email", type: "email", label: "Email" },
      { id: "signature", type: "signature", label: "Signature" },
    ],
  },
  {
    category: "Question Fields",
    fields: [
      { id: "short_answer", type: "short_answer", label: "Short Answer" },
      {
        id: "double_short_answer",
        type: "double_short_answer",
        label: "Short Answer Row",
      },
      { id: "long_answer", type: "long_answer", label: "Long Answer" },
      { id: "checkbox", type: "checkbox", label: "Checkbox" },
      {
        id: "checkbox_group",
        type: "checkbox_group",
        label: "Vertical Checklist",
      },
      {
        id: "custom_checkbox_group",
        type: "custom_checkbox_group",
        label: "Checkbox Row",
      },
      {
        id: "radio_button_group",
        type: "radio_button_group",
        label: "Radio Button Group",
      },
      { id: "table", type: "table", label: "Table" },
      {
        id: "dropdown_select",
        type: "dropdown_select",
        label: "Dropdown Select",
      },
      {
        id: "dynamic_question_group",
        type: "dynamic_question_group",
        label: "Dynamic Question Group",
      },
    ],
  },
  {
    category: "Media Fields",
    fields: [
      { id: "image", type: "image", label: "Image" },
      { id: "image_upload", type: "image_upload", label: "Image Upload" },
    ],
  },
  {
    category: "Date/Time Fields",
    fields: [
      { id: "datetime", type: "datetime", label: "Date & Time" },
      { id: "date_range", type: "date_range", label: "Start Date + End Date" },
      { id: "time_range", type: "time_range", label: "Start Time + End Time" },
    ],
  },
  {
    category: "Forsite Integrations",
    fields: [
      {
        id: "site_information_viewer",
        type: "site_information_viewer",
        label: "Site Information Viewer",
      },
      { id: "email_dropdown", type: "email_dropdown", label: "Email Dropdown" },
      {
        id: "document_checker",
        type: "document_checker",
        label: "Document Checker",
      },
      {
        id: "hazard_register",
        type: "hazard_register",
        label: "Hazard Register",
      },
      {
        id: "user_multi_select",
        type: "user_multi_select",
        label: "Other Company Members",
      },
    ],
  },
];

function FormBuilder() {
  const [formType, setFormType] = useState("Induction");
  const [companyId, setCompanyId] = useState("");
  const [modules, setModules] = useState([]);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [formName, setFormName] = useState("");
  const [confirmedCompanyId, setConfirmedCompanyId] = useState("");
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [isAnyModuleRequiresApproval, setIsAnyModuleRequiresApproval] =
    useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [availableQuestionnaires, setAvailableQuestionnaires] = useState([]);
  const [loadingCompanyId, setLoadingCompanyId] = useState(false);
  const [companyIdError, setCompanyIdError] = useState("");
  const [companyIdSuccess, setCompanyIdSuccess] = useState(false);

  // Styling state
  const [styles, setStyles] = useState({
    buttonColor: "#1976d2",
    bannerColor: "#f5f5f5",
    header: {
      fontSize: "h5",
      fontFamily: "Roboto",
      color: "#000000",
    },
    subheader: {
      fontSize: "h6",
      fontFamily: "Roboto",
      color: "#000000",
    },
  });

  const debouncedHandleSearchChange = useMemo(
    () => debounce((e) => setSearchTerm(e.target.value), 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedHandleSearchChange.cancel();
    };
  }, [debouncedHandleSearchChange]);

  useEffect(() => {
    const anyApprovalRequired = modules.some((mod) => mod.approvalRequired);
    setIsAnyModuleRequiresApproval(anyApprovalRequired);
  }, [modules]);

  const filteredCategories = AVAILABLE_FIELDS.map((category) => ({
    ...category,
    fields: category.fields.filter((field) =>
      field.label.toLowerCase().includes(searchTerm.toLowerCase()),
    ),
  })).filter((category) => category.fields.length > 0);

  const handleConfirmCompanyId = () => {
    if (!companyId.trim()) {
      setCompanyIdError("Company ID cannot be empty.");
      setCompanyIdSuccess(false);
      return;
    }
    setConfirmedCompanyId(Number(companyId));
    setCompanyIdSuccess(true);
  };

  const onDragEnd = (result) => {
    const { source, destination, draggableId, type } = result;
    if (!destination) return;
    if (type === "module") {
      if (
        source.droppableId === "availableFields" &&
        destination.droppableId === "formCanvas"
      ) {
        const field = AVAILABLE_FIELDS.flatMap(
          (category) => category.fields,
        ).find((field) => field.id === draggableId);
        if (!field) {
          console.error(`Field with id "${draggableId}" not found.`);
          return;
        }
        const newModule = createModule(field.type);
        const newModules = Array.from(modules);
        newModules.splice(destination.index, 0, newModule);
        setModules(newModules);
        return;
      }
      if (
        source.droppableId === "formCanvas" &&
        destination.droppableId === "formCanvas"
      ) {
        const reorderedModules = Array.from(modules);
        const [removed] = reorderedModules.splice(source.index, 1);
        reorderedModules.splice(destination.index, 0, removed);
        setModules(reorderedModules);
        return;
      }
    }
    if (type === "childModule") {
      const sourceBannerId = source.droppableId;
      const destBannerId = destination.droppableId;
      if (sourceBannerId === destBannerId) {
        const banner = modules.find((mod) => mod.id === sourceBannerId);
        if (!banner) return;
        const newChildren = Array.from(banner.children);
        const [movedModule] = newChildren.splice(source.index, 1);
        newChildren.splice(destination.index, 0, movedModule);
        const updatedBanner = { ...banner, children: newChildren };
        const newModules = modules.map((mod) =>
          mod.id === updatedBanner.id ? updatedBanner : mod,
        );
        setModules(newModules);
      } else {
        const sourceBanner = modules.find((mod) => mod.id === sourceBannerId);
        const destBanner = modules.find((mod) => mod.id === destBannerId);
        if (!sourceBanner || !destBanner) return;
        const sourceChildren = Array.from(sourceBanner.children);
        const [movedModule] = sourceChildren.splice(source.index, 1);
        const destChildren = Array.from(destBanner.children);
        destChildren.splice(destination.index, 0, movedModule);
        const updatedSourceBanner = {
          ...sourceBanner,
          children: sourceChildren,
        };
        const updatedDestBanner = { ...destBanner, children: destChildren };
        const newModules = modules.map((mod) => {
          if (mod.id === updatedSourceBanner.id) return updatedSourceBanner;
          if (mod.id === updatedDestBanner.id) return updatedDestBanner;
          return mod;
        });
        setModules(newModules);
      }
    }
  };

  // Updated createModule function matching new module defaults
  const createModule = (type) => {
    const id = uuidv4();
    const baseModule = {
      id,
      type,
      required: false,
      approvalRequired: false,
      visibilityCondition: null,
      editableWhenStatus: 2, // default value (0 corresponds to "0, valid")
      visibleWhenStatus: 0, // default value (0 corresponds to "0, valid")
    };

    switch (type) {
      case "header":
        return { ...baseModule, text: "Header Text" };
      case "subheader":
        return { ...baseModule, text: "Subheader Text" };
      case "paragraph":
        return { ...baseModule, content: "<p>This is a paragraph.</p>" };
      case "banner":
        return {
          id,
          type: "banner",
          label: "Banner",
          color: styles.bannerColor || "#f5f5f5",
          children: [],
          required: false,
          approvalRequired: false,
        };
      case "short_answer":
        return { ...baseModule, label: "Short Answer", content: "" };
      case "double_short_answer":
        return {
          ...baseModule,
          label: "Double Short Answer",
          answer1Label: "Answer 1",
          answer2Label: "Answer 2",
          answers: ["", ""],
        };
      case "long_answer":
        return { ...baseModule, label: "Long Answer", content: "" };
      case "checkbox":
        return { ...baseModule, label: "Checkbox" };
      case "checkbox_group":
        return {
          ...baseModule,
          label: "Checkbox Group",
          options: ["Option 1", "Option 2"],
          correctResponses: [],
        };
      case "custom_checkbox_group":
        return {
          id,
          type: "custom_checkbox_group",
          label: "Your Question Here",
          options: ["Yes", "No", "N/A"],
          required: false,
          approvalRequired: false,
        };
      case "email_dropdown":
        return {
          ...baseModule,
          label: "Email Dropdown",
          options: [{ label: "Email", value: "email" }],
          multiple: true,
        };
      case "radio_button_group":
        return {
          ...baseModule,
          label: "Radio Button Group",
          options: ["Option 1", "Option 2"],
        };
      case "user_multi_select":
        return {
          ...baseModule,
          label: "Other Company Members",
          selectedUserIds: [],
        };

      case "table":
        return { ...baseModule, columns: ["Column 1", "Column 2"] };
      case "dropdown_select":
        return {
          ...baseModule,
          label: "Dropdown Label",
          options: [{ label: "Option 1", value: "option_1" }],
          multiple: false,
        };
      case "dynamic_question_group":
        return {
          ...baseModule,
          label: "Dynamic Question Group",
          questions: [],
          randomise: 0,
        };
      case "datetime":
        return { ...baseModule, label: "Date & Time" };
      case "date_range":
        return { ...baseModule, label: "Date Range" };
      case "time_range":
        return { ...baseModule, label: "Time Range" };
      case "email":
        return {
          id,
          type,
          label: "Email Address",
          required: true,
          approvalRequired: false,
        };
      case "site_information_viewer":
        return { ...baseModule };
      case "image":
        return {
          ...baseModule,
          url: "https://via.placeholder.com/150",
          alt_text: "Placeholder Image",
        };
      case "image_upload":
        return {
          ...baseModule,
          label: "Image Upload",
          imageLimit: 5,
          images: [],
        };
      case "document_checker":
        return { ...baseModule, selectedQuestionnaireId: "" };
      case "hazard_register":
        return { ...baseModule, label: "Hazard Register" };
      case "signature":
        return { ...baseModule, label: "Signature" };
      case "user_details":
        return {
          ...baseModule,
          userDetails: {
            firstName: "",
            lastName: "",
            phoneNumber: "",
            company: "",
            email: "",
          },
        };
      default:
        return baseModule;
    }
  };

  // Document handling functions
  const addDocument = () => {
    const newDoc = { id: uuidv4(), name: "", required: false };
    setRequiredDocuments([...requiredDocuments, newDoc]);
  };

  const updateDocument = (id, field, value) => {
    const updatedDocs = requiredDocuments.map((doc) =>
      doc.id === id ? { ...doc, [field]: value } : doc,
    );
    setRequiredDocuments(updatedDocs);
  };

  const removeDocument = (id) => {
    const updatedDocs = requiredDocuments.filter((doc) => doc.id !== id);
    setRequiredDocuments(updatedDocs);
  };

  // Handlers for module changes
  const handleModuleChange = (index, updatedModule) => {
    const newModules = [...modules];
    newModules[index] = updatedModule;
    setModules(newModules);
  };

  // Use API_BASE_URL for axios calls

  const saveForm = async () => {
    const formStructure = {
      title: `${formType} Form`,
      formType: formType,
      type: formName,
      company_id: companyId,
      modules: modules.map((module) => {
        if (module.type === "dynamic_question_group") {
          return {
            ...module,
            questions: module.questions.map((q) => ({
              ...q,
              id: q.id || uuidv4(),
            })),
          };
        }
        return module;
      }),
      requiredDocuments,
      approvalRequired: isAnyModuleRequiresApproval,
      styles,
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/api/forms`, {
        form_structure: formStructure,
        form_type: formType,
        company_id: companyId,
        name: formName,
      });
      const { questionnaireId, formUrl } = response.data;
      alert(
        `Form saved successfully!\nForm ID: ${questionnaireId}\nForm URL: ${formUrl}`,
      );
    } catch (error) {
      console.error("Error saving form:", error);
      alert("An error occurred while saving the form.");
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Grid container spacing={2} sx={{ height: "100vh", padding: 2 }}>
        {/* Left Column: Available Fields */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              padding: 2,
              height: { xs: "auto", md: "100vh" },
              overflowY: "auto",
              position: { xs: "static", md: "sticky" },
              top: { md: 0 },
            }}
          >
            <Typography variant="h6" gutterBottom>
              Available Fields
            </Typography>
            <TextField
              placeholder="Search Fields"
              onChange={debouncedHandleSearchChange}
              value={searchTerm}
              fullWidth
              variant="outlined"
              size="small"
              sx={{ marginBottom: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm && (
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm("")}
                        aria-label="clear search"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <Droppable
              droppableId="availableFields"
              type="module"
              isDropDisabled={true}
            >
              {(provided) => (
                <Box ref={provided.innerRef} {...provided.droppableProps}>
                  {filteredCategories.map((category, catIndex) => (
                    <Accordion key={catIndex} disableGutters>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel-content-${catIndex}`}
                        id={`panel-header-${catIndex}`}
                        sx={{
                          backgroundColor:
                            CATEGORY_COLORS[category.category] || "#e0e0e0",
                          borderRadius: 0,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          {category.category}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List>
                          {category.fields.map((field, index) => (
                            <DraggableModuleItem
                              key={field.id}
                              field={field}
                              index={index}
                            />
                          ))}
                        </List>
                        {provided.placeholder}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </Paper>
        </Grid>

        {/* Center Column: Form Canvas */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{ padding: 2, height: "100%", overflowY: "auto" }}
          >
            <Typography variant="h6" gutterBottom>
              Form Canvas
            </Typography>
            <Droppable droppableId="formCanvas" type="module">
              {(provided) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    minHeight: "80vh",
                    border: "2px dashed #ccc",
                    padding: 2,
                    borderRadius: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  {modules.map((module, index) => (
                    <Draggable
                      key={module.id}
                      draggableId={module.id}
                      index={index}
                    >
                      {(provided) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{ marginBottom: 2 }}
                        >
                          <ModuleEditor
                            module={module}
                            onChange={(updatedModule) =>
                              handleModuleChange(index, updatedModule)
                            }
                            onDelete={() => {
                              const newModules = [...modules];
                              newModules.splice(index, 1);
                              setModules(newModules);
                            }}
                            isEditable={true}
                            availableQuestionnaires={availableQuestionnaires}
                            confirmedCompanyId={confirmedCompanyId}
                            setAvailableQuestionnaires={
                              setAvailableQuestionnaires
                            }
                            modules={modules}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </Paper>
        </Grid>

        {/* Right Column: Settings */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={3}
            sx={{
              padding: 2,
              height: "100vh",
              overflowY: "auto",
              position: "sticky",
              top: 0,
            }}
          >
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="h6" gutterBottom>
                Settings
              </Typography>
            </Box>

            {/* Company ID Section */}
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="company-id-content"
                id="company-id-header"
              >
                <Typography variant="subtitle1">Company ID</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    marginBottom: 2,
                  }}
                >
                  <TextField
                    label="Company ID"
                    value={companyId}
                    onChange={(e) => setCompanyId(e.target.value)}
                    variant="outlined"
                    fullWidth
                    disabled={loadingCompanyId}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirmCompanyId}
                    disabled={
                      loadingCompanyId || companyId === confirmedCompanyId
                    }
                    sx={{
                      color: "black",
                      backgroundColor: "#ffd902",
                      borderRadius: "20px",
                    }}
                  >
                    {loadingCompanyId ? "Confirming..." : "Confirm"}
                  </Button>
                </Box>
                {companyIdError && (
                  <Typography variant="body2" color="error" gutterBottom>
                    {companyIdError}
                  </Typography>
                )}
                {companyIdSuccess && (
                  <Typography variant="body2" color="success.main" gutterBottom>
                    Company ID confirmed successfully!
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Form Name and Type Section */}
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="form-details-content"
                id="form-details-header"
              >
                <Typography variant="subtitle1">Form Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  label="Form Name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  fullWidth
                  margin="normal"
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Select Form Type</InputLabel>
                  <Select
                    value={formType}
                    onChange={(e) => setFormType(e.target.value)}
                    label="Select Form Type"
                  >
                    <MenuItem value="Induction">Induction</MenuItem>
                    <MenuItem value="Permit">Permit</MenuItem>
                    <MenuItem value="Document">Document</MenuItem>
                  </Select>
                </FormControl>
              </AccordionDetails>
            </Accordion>

            {/* Style Customization Section */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="style-customization-content"
                id="style-customization-header"
              >
                <Typography variant="subtitle1">Style Customization</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ marginRight: 2 }}>
                    Button Color:
                  </Typography>
                  <TextField
                    type="color"
                    value={styles.buttonColor}
                    onChange={(e) =>
                      setStyles({ ...styles, buttonColor: e.target.value })
                    }
                    variant="outlined"
                    sx={{ width: 60, height: 60, padding: 0 }}
                    inputProps={{ style: { padding: 0, border: "none" } }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ marginRight: 2 }}>
                    Banner Color:
                  </Typography>
                  <TextField
                    type="color"
                    value={styles.bannerColor}
                    onChange={(e) =>
                      setStyles({ ...styles, bannerColor: e.target.value })
                    }
                    variant="outlined"
                    sx={{ width: 60, height: 60, padding: 0 }}
                    inputProps={{ style: { padding: 0, border: "none" } }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Header Style Customization Section */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="header-style-content"
                id="header-style-header"
              >
                <Typography variant="subtitle1">Header Style</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Header Font Size</InputLabel>
                  <Select
                    value={styles.header.fontSize}
                    onChange={(e) =>
                      setStyles({
                        ...styles,
                        header: { ...styles.header, fontSize: e.target.value },
                      })
                    }
                    label="Header Font Size"
                  >
                    <MenuItem value="h4">H4</MenuItem>
                    <MenuItem value="h5">H5</MenuItem>
                    <MenuItem value="h6">H6</MenuItem>
                    <MenuItem value="subtitle1">Subtitle1</MenuItem>
                    <MenuItem value="subtitle2">Subtitle2</MenuItem>
                    <MenuItem value="body1">Body1</MenuItem>
                    <MenuItem value="body2">Body2</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Header Font Family"
                  value={styles.header.fontFamily}
                  onChange={(e) =>
                    setStyles({
                      ...styles,
                      header: { ...styles.header, fontFamily: e.target.value },
                    })
                  }
                  fullWidth
                  margin="normal"
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ marginRight: 2 }}>
                    Header Color:
                  </Typography>
                  <TextField
                    type="color"
                    value={styles.header.color}
                    onChange={(e) =>
                      setStyles({
                        ...styles,
                        header: { ...styles.header, color: e.target.value },
                      })
                    }
                    variant="outlined"
                    sx={{ width: 60, height: 60, padding: 0 }}
                    inputProps={{ style: { padding: 0, border: "none" } }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Subheader Style Customization Section */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="subheader-style-content"
                id="subheader-style-header"
              >
                <Typography variant="subtitle1">Subheader Style</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Subheader Font Size</InputLabel>
                  <Select
                    value={styles.subheader.fontSize}
                    onChange={(e) =>
                      setStyles({
                        ...styles,
                        subheader: {
                          ...styles.subheader,
                          fontSize: e.target.value,
                        },
                      })
                    }
                    label="Subheader Font Size"
                  >
                    <MenuItem value="h4">H4</MenuItem>
                    <MenuItem value="h5">H5</MenuItem>
                    <MenuItem value="h6">H6</MenuItem>
                    <MenuItem value="subtitle1">Subtitle1</MenuItem>
                    <MenuItem value="subtitle2">Subtitle2</MenuItem>
                    <MenuItem value="body1">Body1</MenuItem>
                    <MenuItem value="body2">Body2</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Subheader Font Family"
                  value={styles.subheader.fontFamily}
                  onChange={(e) =>
                    setStyles({
                      ...styles,
                      subheader: {
                        ...styles.subheader,
                        fontFamily: e.target.value,
                      },
                    })
                  }
                  fullWidth
                  margin="normal"
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ marginRight: 2 }}>
                    Subheader Color:
                  </Typography>
                  <TextField
                    type="color"
                    value={styles.subheader.color}
                    onChange={(e) =>
                      setStyles({
                        ...styles,
                        subheader: {
                          ...styles.subheader,
                          color: e.target.value,
                        },
                      })
                    }
                    variant="outlined"
                    sx={{ width: 60, height: 60, padding: 0 }}
                    inputProps={{ style: { padding: 0, border: "none" } }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Approval Requirement Section */}
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="approval-requirement-content"
                id="approval-requirement-header"
              >
                <Typography variant="subtitle1">
                  Approval Requirement
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Tooltip
                  title={
                    isAnyModuleRequiresApproval
                      ? "At least one module requires approval."
                      : "No modules require approval."
                  }
                >
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isAnyModuleRequiresApproval}
                          disabled
                          color="primary"
                        />
                      }
                      label={
                        isAnyModuleRequiresApproval
                          ? "Approval Required"
                          : "No Approval Required"
                      }
                    />
                  </Box>
                </Tooltip>
              </AccordionDetails>
            </Accordion>

            {/* Requires Documents Section */}
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="requires-documents-content"
                id="requires-documents-header"
              >
                <Typography variant="subtitle1">Requires Documents</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {requiredDocuments.map((doc, index) => (
                    <ListItem key={doc.id}>
                      <TextField
                        label={`Document ${index + 1}`}
                        value={doc.name}
                        onChange={(e) =>
                          updateDocument(doc.id, "name", e.target.value)
                        }
                        fullWidth
                        margin="dense"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={doc.required}
                            onChange={(e) =>
                              updateDocument(
                                doc.id,
                                "required",
                                e.target.checked,
                              )
                            }
                            color="primary"
                          />
                        }
                        label="Required"
                        sx={{ marginLeft: 2 }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => removeDocument(doc.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addDocument}
                  sx={{ marginTop: 1 }}
                >
                  Add Document
                </Button>
              </AccordionDetails>
            </Accordion>

            {/* Save Form Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={saveForm}
              fullWidth
              sx={{
                marginTop: 4,
                color: "black",
                backgroundColor: "#ffd902",
                borderRadius: "40px",
              }}
            >
              Save Form
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </DragDropContext>
  );
}

export default FormBuilder;
